'use client';
import React, { useRef, useEffect, useState } from 'react';

const freeResources = [
  {
    icon: '🧘',
    category: 'Free Meditation',
    title: 'Guided Healing Meditation',
    description: 'A powerful 15-minute guided meditation for stress relief, energy cleansing, and inner peace. Available for free download.',
    cta: 'Download Free',
    href: 'https://wa.me/917049753125?text=Hello%2C%20I%20would%20like%20to%20access%20the%20free%20guided%20meditation',
    color: '#9b7fe8',
  },
  {
    icon: '▶️',
    category: 'YouTube Videos',
    title: 'Healing & Wellness Channel',
    description: 'Watch free healing tutorials, meditation guides, spiritual wisdom, and client testimonials on our YouTube channel.',
    cta: 'Watch Now',
    href: 'https://www.youtube.com/@ShreeVidhaHealingHubb',
    color: '#c9a84c',
  },
  {
    icon: '📝',
    category: 'Blog Articles',
    title: 'Healing Wisdom Blog',
    description: 'Read in-depth articles on energy healing, emotional wellness, spiritual growth, and practical healing tips.',
    cta: 'Read Articles',
    href: 'https://wa.me/917049753125?text=Hello%2C%20I%20would%20like%20to%20access%20your%20blog%20articles',
    color: '#7c5cbf',
  },
  {
    icon: '💡',
    category: 'Healing Tips',
    title: 'Daily Healing Practices',
    description: 'Simple, practical healing tips and daily practices you can incorporate into your routine for ongoing wellness and transformation.',
    cta: 'Get Tips',
    href: 'https://chat.whatsapp.com/JBAssqjgZSQ89CRz3EMpqn',
    color: '#b8a0e8',
  },
];

export default function FreeResourcesSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="free-resources"
      className="relative py-20 overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #130a24 0%, #1a0d30 50%, #130a24 100%)' }}
    >
      <div className="absolute inset-0 lotus-bg opacity-30 pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute left-0 top-1/2 -translate-y-1/2 glow-orb-lavender" style={{ width: '400px', height: '400px', opacity: 0.35 }} />
      </div>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={ref}
          className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#c9a84c]/60" />
              <span className="text-[#c9a84c] text-xs tracking-[0.4em] uppercase font-medium">Free Resources</span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#c9a84c]/60" />
            </div>
            <h2
              className="font-serif text-4xl md:text-5xl font-bold mb-4"
              style={{
                background: 'linear-gradient(135deg, #c4b5fd 0%, #9b7fe8 50%, #c9a84c 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Free Healing Resources
            </h2>
            <div className="section-divider mb-4" />
            <p className="text-[#c4b5fd]/65 max-w-2xl mx-auto text-sm leading-relaxed">
              Begin your healing journey with these free resources — meditations, videos, articles, and daily practices to support your transformation.
            </p>
          </div>

          {/* Resources Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {freeResources?.map((resource, index) => (
              <div
                key={resource?.title}
                className="rounded-2xl p-6 flex flex-col transition-all duration-300 hover:scale-[1.02]"
                style={{
                  background: 'rgba(22, 12, 40, 0.80)',
                  border: `1px solid ${resource?.color}30`,
                  boxShadow: `0 4px 20px ${resource?.color}10`,
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.7s ease ${index * 100}ms, transform 0.7s ease ${index * 100}ms`,
                }}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl mb-4"
                  style={{ background: `${resource?.color}15`, border: `1px solid ${resource?.color}30` }}
                >
                  {resource?.icon}
                </div>
                <div
                  className="text-xs tracking-widest uppercase font-medium mb-2"
                  style={{ color: resource?.color }}
                >
                  {resource?.category}
                </div>
                <h3 className="font-serif text-base font-semibold text-[#e8dff5] mb-3">{resource?.title}</h3>
                <p className="text-[#c4b5fd]/60 text-xs leading-relaxed flex-1 mb-5">{resource?.description}</p>
                <a
                  href={resource?.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-xs font-semibold tracking-wide transition-all duration-300 hover:scale-105"
                  style={{
                    background: `${resource?.color}15`,
                    border: `1px solid ${resource?.color}40`,
                    color: resource?.color,
                  }}
                >
                  {resource?.cta} →
                </a>
              </div>
            ))}
          </div>

          {/* Join Community CTA */}
          <div className="text-center mt-10">
            <div
              className="inline-flex flex-col sm:flex-row items-center gap-4 px-8 py-5 rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, rgba(201,168,76,0.08) 0%, rgba(124,92,191,0.10) 100%)',
                border: '1px solid rgba(201,168,76,0.25)',
              }}
            >
              <div className="text-center sm:text-left">
                <p className="text-[#e8dff5] font-serif text-base font-semibold">Join Our WhatsApp Community</p>
                <p className="text-[#c4b5fd]/55 text-xs mt-1">Get free healing tips, meditations & updates directly on WhatsApp</p>
              </div>
              <a
                href="https://chat.whatsapp.com/JBAssqjgZSQ89CRz3EMpqn"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-xs font-semibold transition-all duration-300 hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #25D366 0%, #1ebe5d 100%)',
                  color: '#fff',
                  boxShadow: '0 4px 15px rgba(37,211,102,0.3)',
                }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Join Community
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
